/**
 * Mobile view of the first quick entry. Rendered only by `../QuickEntry.tsx`, which no screen
 * renders; the live mobile view is `V2/QuickEntryMobileV2.tsx`. The component inside is named
 * `QuickEntryDesktop`; it is the default export of this mobile file. Legacy: don't copy; to be
 * deleted with `../QuickEntry.tsx`.
 *
 * Differs from V2: it uses `QuantityUOM` from `@ryerson/frontend.quantity-uom-inputs` without a
 * `stateMessenger`, and keeps Add Below disabled while an `is-debouncing` true message for
 * "quick-entry" is the latest one received; with no `stateMessenger` on the input, no such
 * message arrives.
 */
import { useApplication } from "@ryerson/frontend.application";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { css } from "@emotion/react";
import React, { useState } from "react";
import { QuickEntryContent, QuickEntryText } from "./QuickEntryContent";
import { QuickEntryProps } from "./QuickEntry.models";
import { Icon } from "@ryerson/frontend.assets";
import { MobileTopNotificationsContainer } from "@components/Shared/CartNotifications/NotificationPopup";
import { QuantityUOM } from "@ryerson/frontend.quantity-uom-inputs";
import { UOMValue } from "@ryerson/common.uom";
import { SearchInput, ProductList, AddBelowButton, PriceUOM } from "./SharedComponents";
import { ErrorMessage, ErrorContainer, QuickEntryContainer } from "./StyledComponents";

/** V1 mobile quick-entry panel; not rendered by any screen. */
const QuickEntryDesktop: React.FC<QuickEntryProps> = ({
	searchInputContainerRef,
	showDropdown,
	setShowDropdown,
	searchResult,
	onClickSelectItem,
	isError,
	quantity,
	setQuantity,
	qtyUOM,
	priceUOM,
	priceUOMDropdown,
	onClickAddBelow,
	displaySearchTerm,
	onChangeText,
	searchTerm,
	setPriceUOM,
	closeQuickEntry,
	selectedProduct,
	itemAddSuccess,
	itemAddFail,
	updateQuantityUom,
	isQuickEntrySubmitted,
	handleValidationResponse,
	hasMounted,
	stateMessenger,
	qtyUOMDropdown,
}) => {
	const { theme } = useTheme();

	const backgroundPrimary = theme.colors.primary.background;
	const backgroundSecondary = theme.colors.secondary.background;
	const headerSecondary = theme.colors.secondary.header;
	const textSecondary = theme.colors.secondary.text;
	const [isDebouncing, setIsDebouncing] = React.useState<boolean>(false);
	const [tempItemNumber, setTempItemNumber] = useState<any>();
	/*pubsub messaging*/
	const [message, setMessage] = React.useState<Array<any>>([]);
	// Publish call to pubsub
	const publishMessage = (messageString: string, data: any, topic: any) => {
		/* note the use of setTimeout is required for publishing messages*/
		setTimeout(() => stateMessenger?.publish(messageString, data, topic), 10);
	};
	const {
		localization: { language },
	} = useApplication();

	const [buttonDisabled, setIsButtonDisabled] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (!quantity || isDebouncing) {
			setIsButtonDisabled(true);
		} else {
			setIsButtonDisabled(false);
		}
	}, [quantity, isDebouncing]);

	const content: QuickEntryText = QuickEntryContent[language] || QuickEntryContent.en;

	React.useEffect(() => {
		// Subscribe to pub-sub  stateMessenger
		stateMessenger?.subscribe((msg: string, value: any, topic: any) =>
			setMessage([msg, value, topic])
		);
		if (hasMounted) {
			const _query = document && (document as any).querySelector.bind(document);
			const elInput = _query("[data-testid=quick-entry-search-input]");
			elInput?.focus();
		}
	}, [hasMounted]);

	/** @desc -Listener for Pub Sub Messages from other components 	*/
	/* Note: the use of publishing messages (pubsub) to receive messages from quantityUOm Component
			if value is true  of "is-debouncing" then we are currently debouncing 
	*/
	React.useEffect(() => {
		let [messageString, value, id] = message;

		if (messageString === "is-debouncing" && id === "quick-entry") {
			setIsDebouncing(value);
		}
	}, [message]);

	return (
		<>
			<MobileTopNotificationsContainer />
			<ContentSection vPadding="0" hPadding="0">
				<QuickEntryContainer
					{...{ isMobile: true }}
					direction="column"
					css={{ background: backgroundSecondary }}
				>
					<Flex css={{ paddingBottom: "20px" }} justifyContent="space-between">
						<Typography color={headerSecondary}>{content.quickEntry}</Typography>
						<Icon onClick={closeQuickEntry} icon="close" circled={true} />
					</Flex>
					<FlexItem css={{ width: "100%" }}>
						<Flex
							itemRef={searchInputContainerRef}
							direction="column"
							style={css`
								width: 100%;
							`}
						>
							<SearchInput
								inputId="quick-entry-search-input"
								displaySearchTerm={displaySearchTerm}
								handleChange={(e) => onChangeText(e)}
								handleFocus={() => setShowDropdown(true)}
							/>
							{showDropdown && (
								<ProductList
									searchResults={searchResult}
									handleClick={onClickSelectItem}
								/>
							)}
							{isError && (
								<ErrorContainer {...{ color: textSecondary }}>
									<ErrorMessage
										{...{ bgColor: backgroundPrimary, bColor: textSecondary }}
									>
										{"No Results Found".replace(
											"%searchString%",
											searchTerm.toString()
										)}
									</ErrorMessage>
								</ErrorContainer>
							)}
						</Flex>
					</FlexItem>
					<Flex alignItems="flex-end" css={{ width: "100%", paddingTop: "20px" }}>
						<Flex
							direction="column"
							style={css`
								padding-right: 10px;
								width: 100%;
							`}
						>
							<QuantityUOM
								{...{
									id: "quick-entry",
									itemData: selectedProduct || {},
									quantityUomOptions: qtyUOMDropdown,
									size: "sm",
									type: "primary",
									disabled: !selectedProduct,
									dataTestId: "quantity-uom-test",
									showLabel: true,
									isFocused: !!selectedProduct,
									isMobile: true,
								}}
								isSubmitted={isQuickEntrySubmitted}
								quantityValue={quantity}
								uomValue={qtyUOM as UOMValue}
								handleChange={(quantityValue, uomValue) => {
									setQuantity(quantityValue);
									updateQuantityUom(uomValue);
								}}
								handleValidationResponse={handleValidationResponse}
							/>
						</Flex>
					</Flex>
					<Flex
						direction="column"
						style={css`
							padding-top: 20px;
							width: 100px;
							padding-right: 10px;
						`}
					>
						<div
							onFocus={() => publishMessage(`to-debounce`, "focus", "quick-entry")}
							onChange={(value) =>
								publishMessage(`to-debounce`, "change", "quick-entry")
							}
						>
							<PriceUOM
								isMobile={true}
								handleChange={setPriceUOM}
								isDisabled={() => {
									return !selectedProduct || priceUOMDropdown.length === 1;
								}}
								priceUOM={priceUOM}
								priceUOMDropdown={priceUOMDropdown}
							/>
						</div>
					</Flex>

					<FlexItem css={{ width: "100%" }}>
						<AddBelowButton
							isMobile={true}
							itemNumber={tempItemNumber}
							itemAddSuccess={itemAddSuccess}
							itemAddFail={itemAddFail}
							handleClick={() => {
								setTempItemNumber(selectedProduct?.partNumber);
								onClickAddBelow();
							}}
							isDisabled={buttonDisabled}
							buttonClassName={""}
						/>
					</FlexItem>
				</QuickEntryContainer>
			</ContentSection>
		</>
	);
};

export default QuickEntryDesktop;
